/**
 * Plans Service
 *
 * Type-safe plan management service using @monorepo/shared schemas
 */

import type { EntityStatus } from '@monorepo/shared/schemas/common'
import {
  type CreatePlan,
  type CreatePlanAgentAction,
  CreatePlanAgentActionSchema,
  type CreatePlanCharge,
  CreatePlanChargeSchema,
  type CreatePlanFeature,
  CreatePlanFeatureSchema,
  CreatePlanSchema,
  type CreatePricingTier,
  CreatePricingTierSchema,
  type PaginatedPlansResponse,
  PaginatedPlansResponseSchema,
  type Plan,
  type PlanAgentAction,
  PlanAgentActionSchema,
  type PlanCharge,
  PlanChargeSchema,
  type PlanFeature,
  PlanFeatureSchema,
  type PlanQuery,
  type PlanResponse,
  PlanResponseSchema,
  type PricingTier,
  PricingTierSchema,
  type UpdatePlan,
  type UpdatePlanAgentAction,
  UpdatePlanAgentActionSchema,
  type UpdatePlanCharge,
  UpdatePlanChargeSchema,
  type UpdatePlanFeature,
  UpdatePlanFeatureSchema,
  UpdatePlanSchema,
  type UpdatePricingTier,
  UpdatePricingTierSchema,
} from '@monorepo/shared/schemas/plans'
import { del, get, patch, post, put } from '@/lib/api-client'

export const planService = {
  /**
   * List all plans (filtered by query)
   */
  async getPlans(query?: PlanQuery): Promise<PaginatedPlansResponse> {
    const queryParams = new URLSearchParams()

    if (query?.category) queryParams.append('category', query.category)
    if (query?.plan_type) queryParams.append('plan_type', query.plan_type)
    if (query?.status) queryParams.append('status', query.status)
    if (query?.target_audience)
      queryParams.append('target_audience', query.target_audience)
    if (query?.billing_period)
      queryParams.append('billing_period', query.billing_period)
    if (query?.currency) queryParams.append('currency', query.currency)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.code) queryParams.append('code', query.code)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    const url = `/plans${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedPlansResponseSchema)
  },

  /**
   * Get plan by ID (includes charges, agent_actions, features)
   */
  async getPlan(id: string): Promise<PlanResponse> {
    return get(`/plans/${id}`, PlanResponseSchema)
  },

  /**
   * Create new plan
   */
  async createPlan(data: CreatePlan): Promise<PlanResponse> {
    const validated = CreatePlanSchema.parse(data)

    return post('/plans', validated, PlanResponseSchema)
  },

  /**
   * Update plan
   */
  async updatePlan(id: string, data: UpdatePlan): Promise<PlanResponse> {
    const validated = UpdatePlanSchema.parse(data)

    return patch(`/plans/${id}`, validated, PlanResponseSchema)
  },

  /**
   * Delete plan (soft delete)
   */
  async deletePlan(id: string): Promise<void> {
    await del(`/plans/${id}`)
  },

  /**
   * Update plan status
   */
  async updateStatus(id: string, status: EntityStatus): Promise<PlanResponse> {
    const validated = UpdatePlanSchema.parse({ status })

    return patch(`/plans/${id}`, validated, PlanResponseSchema)
  },

  /**
   * Clone plan (creates copy with new code)
   */
  async clonePlan(
    id: string,
    code: string,
    name?: string
  ): Promise<PlanResponse> {
    return post(`/plans/${id}/clone`, { code, name }, PlanResponseSchema)
  },

  /**
   * Check if plan code is available/unique
   */
  async checkCodeAvailable(code: string): Promise<boolean> {
    const response = (await get(
      `/plans/check-code/${encodeURIComponent(code)}`
    )) as { available?: boolean }
    return response.available || false
  },

  // ============================================================================
  // PLAN CHARGES
  // ============================================================================

  /**
   * Add charge to plan
   */
  async addCharge(planId: string, data: CreatePlanCharge): Promise<PlanCharge> {
    const validated = CreatePlanChargeSchema.parse(data)

    return post(`/plans/${planId}/charges`, validated, PlanChargeSchema)
  },

  /**
   * Update charge
   */
  async updateCharge(
    planId: string,
    chargeId: string,
    data: UpdatePlanCharge
  ): Promise<PlanCharge> {
    const validated = UpdatePlanChargeSchema.parse(data)

    return patch(
      `/plans/${planId}/charges/${chargeId}`,
      validated,
      PlanChargeSchema
    )
  },

  /**
   * Remove charge from plan
   */
  async removeCharge(planId: string, chargeId: string): Promise<void> {
    await del(`/plans/${planId}/charges/${chargeId}`)
  },

  // ============================================================================
  // PLAN FEATURES
  // ============================================================================

  /**
   * Add feature to plan
   */
  async addFeature(
    planId: string,
    data: CreatePlanFeature
  ): Promise<PlanFeature> {
    const validated = CreatePlanFeatureSchema.parse(data)

    return post(`/plans/${planId}/features`, validated, PlanFeatureSchema)
  },

  /**
   * Update plan feature
   */
  async updateFeature(
    planId: string,
    featureId: string,
    data: UpdatePlanFeature
  ): Promise<PlanFeature> {
    const validated = UpdatePlanFeatureSchema.parse(data)

    return put(
      `/plans/${planId}/features/${featureId}`,
      validated,
      PlanFeatureSchema
    )
  },

  /**
   * Remove feature from plan
   */
  async removeFeature(planId: string, featureId: string): Promise<void> {
    await del(`/plans/${planId}/features/${featureId}`)
  },

  /**
   * Add pricing tier to plan feature
   */
  async addFeatureTier(
    planId: string,
    featureId: string,
    data: CreatePricingTier
  ): Promise<PricingTier> {
    const validated = CreatePricingTierSchema.parse(data)

    return post(
      `/plans/${planId}/features/${featureId}/tiers`,
      validated,
      PricingTierSchema
    )
  },

  /**
   * Update pricing tier for plan feature
   */
  async updateFeatureTier(
    planId: string,
    featureId: string,
    tierId: string,
    data: UpdatePricingTier
  ): Promise<PricingTier> {
    const validated = UpdatePricingTierSchema.parse(data)

    return put(
      `/plans/${planId}/features/${featureId}/tiers/${tierId}`,
      validated,
      PricingTierSchema
    )
  },

  /**
   * Remove pricing tier from plan feature
   */
  async removeFeatureTier(
    planId: string,
    featureId: string,
    tierId: string
  ): Promise<void> {
    await del(`/plans/${planId}/features/${featureId}/tiers/${tierId}`)
  },

  // ============================================================================
  // PLAN AGENT ACTIONS
  // ============================================================================

  /**
   * Add agent action to plan
   */
  async addAgentAction(
    planId: string,
    data: CreatePlanAgentAction
  ): Promise<PlanAgentAction> {
    const validated = CreatePlanAgentActionSchema.parse(data)

    return post(
      `/plans/${planId}/agent-actions`,
      validated,
      PlanAgentActionSchema
    )
  },

  /**
   * Update plan agent action
   */
  async updateAgentAction(
    planId: string,
    agentActionId: string,
    data: UpdatePlanAgentAction
  ): Promise<PlanAgentAction> {
    const validated = UpdatePlanAgentActionSchema.parse(data)

    return put(
      `/plans/${planId}/agent-actions/${agentActionId}`,
      validated,
      PlanAgentActionSchema
    )
  },

  /**
   * Remove agent action from plan
   */
  async removeAgentAction(
    planId: string,
    agentActionId: string
  ): Promise<void> {
    await del(`/plans/${planId}/agent-actions/${agentActionId}`)
  },

  /**
   * Add pricing tier to plan agent action
   */
  async addAgentActionTier(
    planId: string,
    agentActionId: string,
    data: CreatePricingTier
  ): Promise<PricingTier> {
    const validated = CreatePricingTierSchema.parse(data)

    return post(
      `/plans/${planId}/agent-actions/${agentActionId}/tiers`,
      validated,
      PricingTierSchema
    )
  },

  /**
   * Update pricing tier for plan agent action
   */
  async updateAgentActionTier(
    planId: string,
    agentActionId: string,
    tierId: string,
    data: UpdatePricingTier
  ): Promise<PricingTier> {
    const validated = UpdatePricingTierSchema.parse(data)

    return put(
      `/plans/${planId}/agent-actions/${agentActionId}/tiers/${tierId}`,
      validated,
      PricingTierSchema
    )
  },

  /**
   * Remove pricing tier from plan agent action
   */
  async removeAgentActionTier(
    planId: string,
    agentActionId: string,
    tierId: string
  ): Promise<void> {
    await del(`/plans/${planId}/agent-actions/${agentActionId}/tiers/${tierId}`)
  },
}

// Re-export types for convenience
export type {
  Plan,
  CreatePlan,
  UpdatePlan,
  PlanResponse,
  PaginatedPlansResponse,
  PlanQuery,
  CreatePlanCharge,
  UpdatePlanCharge,
  PlanCharge,
  CreatePlanFeature,
  UpdatePlanFeature,
  PlanFeature,
  CreatePlanAgentAction,
  UpdatePlanAgentAction,
  PlanAgentAction,
  CreatePricingTier,
  UpdatePricingTier,
  PricingTier,
}

// Default export for backward compatibility
export default planService
